import { api } from "@/app/app.api";
import { objectToFormData } from "@/shared/utils/data";
import { RequestMethod } from "@/shared/utils/RequestMethod";
import { camelToSnake, snakeToCamel } from "caseparser";

export const transactionsApi = api
  .enhanceEndpoints({ addTagTypes: ["bills"] })
  .injectEndpoints({
    overrideExisting: false,
    endpoints: (builder) => ({
      bills: builder.query({
        query: ({
          subject,
          createdByIds,
          approvedByIds,
          subcategories,
          branchId,
          currency,
          minAmount,
          maxAmount,
          startDate,
          endDate,
        }) => {
          const params = new URLSearchParams();

          // Add simple parameters
          if (subject) params.append("subject", subject);
          if (startDate) params.append("start_date", startDate);
          if (endDate) params.append("end_date", endDate);
          if (currency) params.append("currency", currency);
          if (minAmount !== undefined)
            params.append("min_amount", minAmount.toString());
          if (maxAmount !== undefined)
            params.append("max_amount", maxAmount.toString());

          // Add array parameters correctly
          if (createdByIds && createdByIds.length > 0) {
            createdByIds.forEach((id) =>
              params.append("created_by_ids[]", id.toString())
            );
          }

          if (approvedByIds && approvedByIds.length > 0) {
            approvedByIds.forEach((id) =>
              params.append("approved_by_ids[]", id.toString())
            );
          }

          if (subcategories && subcategories.length > 0) {
            subcategories.forEach((sub) =>
              params.append("subcategories[]", sub)
            );
          }

          return {
            url: `branchs/${branchId}/bills?${params.toString()}`,
            method: "GET",
          };
        },
        transformResponse: (response) => snakeToCamel(response.data),
        providesTags: ["bills"],
      }),

      exportBills: builder.query({
        query({
          branchId,
          subject,
          startDate,
          endDate,
          currency,
          minAmount,
          maxAmount,
          createdByIds,
          approvedByIds,
          subcategories,
        }) {
          return {
            url: `branchs/${branchId}/bills/export`,
            method: RequestMethod.GET,
            responseHandler: async (response) =>
              window.location.assign(
                window.URL.createObjectURL(await response.blob())
              ),
            params: camelToSnake({
              subject,
              startDate,
              endDate,
              currency,
              minAmount,
              maxAmount,
              createdByIds,
              approvedByIds,
              subcategories,
            }),
            cache: "no-cache",
          };
        },
      }),
      createBill: builder.mutation({
        query: ({
          branchId,
          date,
          time,
          subject,
          description,
          amount,
          selledBy,
          approvedBy,
          subcategory,
          invoice,
          currency,
          xml,
        }) => {
          const formData = new FormData();

          formData.append("date", date);
          formData.append("time", time);
          formData.append("subject", subject);
          formData.append("description", description);
          formData.append("amount", String(amount));
          formData.append("selled_by", selledBy);
          formData.append("approved_by", String(approvedBy));
          formData.append("subcategory", subcategory);
          formData.append("currency", currency);

          if (invoice) {
            formData.append("invoice", invoice);
          }

          if (xml) {
            formData.append("xml", xml);
          }

          return {
            url: `branchs/${branchId}/bills`,
            method: "POST",
            body: formData,
          };
        },
        invalidatesTags: ["bills"],
      }),
      updateBill: builder.mutation({
        query: ({
          id,
          branchId,
          date,
          time,
          subject,
          description,
          amount,
          selledBy,
          approvedBy,
          subcategory,
          invoice,
          currency,
          xml,
        }) => ({
          url: `branchs/${branchId}/bills/${id}`,
          method: RequestMethod.POST,
          body: objectToFormData(
            camelToSnake({
              _method: "PUT",
              branchId,
              date,
              time,
              subject,
              description,
              amount,
              selledBy,
              approvedBy,
              subcategory,
              invoice,
              currency,
              xml,
            })
          ),
          formData: true,
        }),
        invalidatesTags: ["bills"],
      }),
      deleteBill: builder.mutation({
        query: ({ branchId, id }) => ({
          url: `branchs/${branchId}/bills/${id}`,
          method: RequestMethod.DELETE,
        }),
        invalidatesTags: ["bills"],
      }),
    }),
  });

export const {
  useBillsQuery,
  useCreateBillMutation,
  useUpdateBillMutation,
  useDeleteBillMutation,
  useLazyExportBillsQuery,
} = transactionsApi;
